import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  FileText,
  Users,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Shield,
  CreditCard,
  Activity,
  Key,
  UserCog,
  Layers,
  Menu,
  X,
  BookOpen,
  Megaphone,
  Mail,
  Crown,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  // Props removidas para usar useAuth internamente
}

const teacherLinks = [
  { href: '/dashboard', label: 'Início', icon: Home },
  { href: '/dashboard/turmas', label: 'Turmas', icon: Users },
  { href: '/dashboard/provas', label: 'Avaliações', icon: FileText },
  { href: '/dashboard/planos-de-aula', label: 'Planos de Aula', icon: BookOpen },
  { href: '/dashboard/relatorios', label: 'Relatórios', icon: BarChart3 },
  { href: '/dashboard/assinatura', label: 'Assinatura', icon: Crown },
  { href: '/dashboard/configuracoes', label: 'Configurações', icon: Settings },
];

const adminLinks = [
  { href: '/dashboard/admin', label: 'Painel Admin', icon: Shield },
  { href: '/dashboard/admin/users', label: 'Usuários', icon: UserCog },
  { href: '/dashboard/admin/plans', label: 'Planos', icon: Layers },
  { href: '/dashboard/admin/finance', label: 'Financeiro', icon: CreditCard },
  { href: '/dashboard/admin/logs', label: 'Logs', icon: Activity },
  { href: '/dashboard/admin/llm', label: 'Config. LLM', icon: Key },
  { href: '/dashboard/admin/notifications', label: 'Notificações', icon: Megaphone },
  { href: '/dashboard/admin/emails', label: 'Emails', icon: Mail },
];

export function Sidebar({}: SidebarProps) {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return null;

  async function handleSignOut() {
    await signOut();
  }

  const isAdmin = user.role === 'admin';

  function NavLink({ href, label, icon: Icon }: { href: string; label: string; icon: React.ElementType }) {
    const isActive = pathname === href || (href !== '/dashboard' && href !== '/dashboard/admin' && pathname.startsWith(href));
    const isExactActive = pathname === href;
    const active = href === '/dashboard' || href === '/dashboard/admin' ? isExactActive : isActive;

    return (
      <Link
        to={href}
        onClick={() => setMobileOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all group ${
          active
            ? 'bg-primary-50 text-primary-700 shadow-sm shadow-primary-100'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      >
        <Icon className={`w-5 h-5 shrink-0 ${active ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
        {!collapsed && <span>{label}</span>}
      </Link>
    );
  }

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-gray-100">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="Prova+" className="object-contain" style={{ height: collapsed ? 28 : 32, width: 'auto' }} />
        </Link>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex w-7 h-7 items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {isAdmin && !collapsed && (
          <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
        )}
        {isAdmin &&
          adminLinks.map((link) => (
            <NavLink key={link.href} href={link.href} label={link.label} icon={link.icon} />
          ))}

        {isAdmin && <div className="my-3 border-t border-gray-100" />}

        {!collapsed && (
          <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {isAdmin ? 'Professor' : 'Menu'}
          </p>
        )}
        {teacherLinks.map((link) => (
          <NavLink key={link.href} href={link.href} label={link.label} icon={link.icon} />
        ))}
      </nav>

      {/* User info */}
      <div className="p-3 border-t border-gray-100">
        {!collapsed && (
          <div className="px-3 py-3 mb-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-secondary-500 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg shadow-primary-500/20">
                {user.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
            <div className="mt-2">
              <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                user.subscription_plan === 'premium'
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                  : user.subscription_plan === 'basic'
                  ? 'bg-teal-100 text-teal-700 border border-teal-200'
                  : 'bg-gray-100 text-gray-600 border border-gray-200'
              }`}>
                {user.subscription_plan === 'premium' ? 'Plano ★ Premium' : 
                 user.subscription_plan === 'basic' ? 'Plano Basic' : 'Plano Free'}
              </span>
            </div>
          </div>
        )}
        <button
          onClick={handleSignOut}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all"
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!collapsed && <span>Sair</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-lg border border-gray-200"
      >
        <Menu className="w-5 h-5 text-gray-700" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col border-r border-gray-200 bg-white transition-all duration-300 shrink-0 ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
